import React, { useState, useEffect, useRef, useCallback } from 'react';

const API_URL = '/api';

// ═══ Portal API helper ═══
function portalApi(token) {
  const headers = { 'Content-Type': 'application/json' };
  if (token) headers['Authorization'] = 'Bearer ' + token;
  return {
    get: async (path) => {
      const res = await fetch(API_URL + '/portal' + path, { headers });
      if (res.status === 401) throw { expired: true };
      return res.json();
    },
    post: async (path, body) => {
      const res = await fetch(API_URL + '/portal' + path, { method: 'POST', headers, body: JSON.stringify(body) });
      if (res.status === 401) throw { expired: true };
      if (!res.ok) { const d = await res.json(); throw d; }
      return res.json();
    },
    upload: async (path, file, extra = {}) => {
      const form = new FormData();
      form.append('file', file);
      Object.entries(extra).forEach(([k, v]) => { if (v) form.append(k, v); });
      const res = await fetch(API_URL + '/portal' + path, {
        method: 'POST',
        headers: { 'Authorization': 'Bearer ' + token },
        body: form,
      });
      if (res.status === 401) throw { expired: true };
      if (!res.ok) { const d = await res.json(); throw d; }
      return res.json();
    },
  };
}

// ═══ Icons (inline SVG) ═══
const Icons = {
  message: (
    <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" /></svg>
  ),
  doc: (
    <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
  ),
  upload: (
    <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" /></svg>
  ),
  activity: (
    <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
  ),
  status: (
    <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" /></svg>
  ),
  send: (
    <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" /></svg>
  ),
  download: (
    <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" /></svg>
  ),
  check: (
    <svg className="h-3.5 w-3.5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" /></svg>
  ),
  logout: (
    <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" /></svg>
  ),
};

// ═══ Format helpers ═══
function timeAgo(dateStr) {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return mins + 'm ago';
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return hrs + 'h ago';
  const days = Math.floor(hrs / 24);
  if (days < 7) return days + 'd ago';
  return new Date(dateStr).toLocaleDateString();
}
function formatDate(dateStr) {
  return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' });
}
function formatSize(bytes) {
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1048576) return (bytes / 1024).toFixed(1) + ' KB';
  return (bytes / 1048576).toFixed(1) + ' MB';
}

// ═══════════════════════════════════════════
// MAIN PORTAL COMPONENT
// ═══════════════════════════════════════════
export default function Portal() {
  const [step, setStep] = useState('verify');
  const [form, setForm] = useState({ first_name: '', last_name: '', date_of_birth: '', ssn_last4: '', reference_number: '' });
  const [verifying, setVerifying] = useState(false);
  const [error, setError] = useState('');
  const [attemptsLeft, setAttemptsLeft] = useState(null);

  // Session state
  const [token, setToken] = useState(null);
  const [caseData, setCaseData] = useState(null);
  const [stages, setStages] = useState([]);
  const [contact, setContact] = useState(null);
  const [firmName, setFirmName] = useState('');
  const [portalBranding, setPortalBranding] = useState(null);
  const [tab, setTab] = useState('status');
  const [unreadCount, setUnreadCount] = useState(0);
  const [pendingDocs, setPendingDocs] = useState(0);

  const api = useRef(null);

  const handleExpired = useCallback(() => {
    setToken(null);
    setStep('verify');
    setError('Your session has expired. Please verify again.');
  }, []);

  // Refresh session data periodically
  const refreshSession = useCallback(async () => {
    if (!api.current) return;
    try {
      const data = await api.current.get('/session');
      setCaseData(data.case);
      setStages(data.stages);
      setFirmName(data.firm_name);
      if (data.portal_branding !== undefined) setPortalBranding(data.portal_branding);
      setUnreadCount(data.unread_messages);
      setPendingDocs(data.pending_doc_requests);
    } catch (err) {
      if (err.expired) handleExpired();
    }
  }, [handleExpired]);

  useEffect(() => {
    if (!token) return;
    const interval = setInterval(refreshSession, 30000);
    return () => clearInterval(interval);
  }, [token, refreshSession]);

  // ═══ Verify ═══
  const handleVerify = async (e) => {
    e.preventDefault();
    setVerifying(true);
    setError('');
    try {
      const res = await fetch(API_URL + '/portal/verify', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.error || 'Verification failed.');
        if (data.attempts_remaining !== undefined) setAttemptsLeft(data.attempts_remaining);
        return;
      }
      if (data.verified) {
        setToken(data.token);
        api.current = portalApi(data.token);
        setContact(data.contact);
        setCaseData(data.case);
        setStages(data.stages || []);
        setFirmName(data.firm_name || '');
        setPortalBranding(data.portal_branding || null);
        setStep(data.closed ? 'closed' : 'verified');
        setTab('status');
      }
    } catch (err) {
      setError('Unable to connect. Please try again later.');
    } finally {
      setVerifying(false);
    }
  };

  const handleLogout = async () => {
    try { await api.current?.post('/logout'); } catch (e) {}
    setToken(null);
    setStep('verify');
    setCaseData(null);
    setStages([]);
    setContact(null);
    setError('');
    setForm({ first_name: '', last_name: '', date_of_birth: '', ssn_last4: '', reference_number: '' });
  };

  // ═══════════════════════════════════════════
  // VERIFICATION FORM
  // ═══════════════════════════════════════════
  if (step === 'verify') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50 flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          <div className="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden">
            <div className="px-8 pt-8 pb-5 text-center">
              <div className="h-14 w-14 rounded-2xl bg-gradient-to-br from-blue-600 to-blue-700 flex items-center justify-center mx-auto mb-4 shadow-lg shadow-blue-200">
                {Icons.doc}
              </div>
              <h1 className="text-2xl font-bold text-gray-900">Client Portal</h1>
              <p className="text-sm text-gray-500 mt-2">Verify your identity to access your case.</p>
            </div>
            <form onSubmit={handleVerify} className="px-8 pb-8 space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1.5">First Name</label>
                  <input type="text" value={form.first_name} onChange={e => setForm({ ...form, first_name: e.target.value })}
                    required autoComplete="given-name" placeholder="John"
                    className="w-full px-3.5 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent focus:bg-white transition-all" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1.5">Last Name</label>
                  <input type="text" value={form.last_name} onChange={e => setForm({ ...form, last_name: e.target.value })}
                    required autoComplete="family-name" placeholder="Smith"
                    className="w-full px-3.5 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent focus:bg-white transition-all" />
                </div>
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1.5">Date of Birth</label>
                <input type="date" value={form.date_of_birth} onChange={e => setForm({ ...form, date_of_birth: e.target.value })}
                  required
                  className="w-full px-3.5 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent focus:bg-white transition-all" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1.5">Last 4 of SSN</label>
                  <input type="text" value={form.ssn_last4}
                    onChange={e => setForm({ ...form, ssn_last4: e.target.value.replace(/\D/g, '').slice(0, 4) })}
                    maxLength={4} placeholder="••••" inputMode="numeric"
                    className="w-full px-3.5 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent focus:bg-white transition-all tracking-[0.3em] text-center font-mono" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1.5">Reference #</label>
                  <input type="text" value={form.reference_number}
                    onChange={e => setForm({ ...form, reference_number: e.target.value })}
                    required placeholder="SMI-1007"
                    className="w-full px-3.5 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent focus:bg-white transition-all" />
                </div>
              </div>
              {error && (
                <div className="flex items-start gap-2.5 p-3.5 bg-red-50 rounded-xl border border-red-100">
                  <svg className="h-4 w-4 text-red-400 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <div>
                    <p className="text-sm text-red-700">{error}</p>
                    {attemptsLeft !== null && attemptsLeft > 0 && attemptsLeft < 10 && (
                      <p className="text-xs text-red-500 mt-0.5">{attemptsLeft} attempt{attemptsLeft !== 1 ? 's' : ''} remaining</p>
                    )}
                  </div>
                </div>
              )}
              <button type="submit" disabled={verifying}
                className="w-full py-3 bg-gradient-to-r from-blue-600 to-blue-700 text-white font-semibold rounded-xl hover:from-blue-700 hover:to-blue-800 disabled:opacity-50 transition-all text-sm shadow-lg shadow-blue-200">
                {verifying ? (
                  <span className="flex items-center justify-center gap-2">
                    <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"/><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/></svg>
                    Verifying...
                  </span>
                ) : 'Access My Portal'}
              </button>
              <p className="text-[10px] text-gray-400 text-center">Your information is verified securely and never stored in your browser.</p>
            </form>
          </div>
        </div>
      </div>
    );
  }

  // ═══════════════════════════════════════════
  // CLOSED CASE
  // ═══════════════════════════════════════════
  if (step === 'closed') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50 flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-8 text-center">
            {firmName && <p className="text-xs text-gray-400 uppercase tracking-wider font-semibold mb-3">{firmName}</p>}
            <div className="h-16 w-16 rounded-full bg-green-50 flex items-center justify-center mx-auto mb-4">
              <svg className="h-8 w-8 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <h1 className="text-xl font-bold text-gray-900 mb-2">Hi {contact?.preferred_name || contact?.first_name}</h1>
            <p className="text-sm text-gray-600 mb-4">Your matter <span className="font-semibold">{caseData?.reference_number}</span> has been closed.</p>
            <p className="text-xs text-gray-400">If you have questions, please contact {firmName || 'your representative'} directly.</p>
            <button onClick={handleLogout} className="mt-6 text-sm text-blue-600 hover:text-blue-800 font-medium">← Go Back</button>
          </div>
        </div>
      </div>
    );
  }

  // ═══════════════════════════════════════════
  // VERIFIED — MAIN PORTAL
  // ═══════════════════════════════════════════
  const currentStageIndex = stages.findIndex(s => s.id === caseData?.workflow_stage_id);
  const tabs = [
    { id: 'status', label: 'Status', icon: Icons.status },
    { id: 'messages', label: 'Messages', icon: Icons.message, badge: unreadCount },
    { id: 'documents', label: 'Documents', icon: Icons.doc, badge: pendingDocs },
    { id: 'activity', label: 'Activity', icon: Icons.activity },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50">
      {/* Top Header */}
      <div className="bg-white border-b border-gray-100 sticky top-0 z-20">
        <div className="max-w-2xl mx-auto px-4 py-3 flex items-center justify-between">
          <div>
            {firmName && <p className="text-[10px] text-gray-400 uppercase tracking-wider font-semibold">{firmName}</p>}
            <p className="text-sm font-bold text-gray-900">Hi {contact?.preferred_name || contact?.first_name} 👋</p>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-[10px] font-mono text-gray-400 bg-gray-50 px-2 py-1 rounded-lg">{caseData?.reference_number}</span>
            <button onClick={handleLogout} className="text-gray-400 hover:text-gray-600 transition-colors" title="Log out">{Icons.logout}</button>
          </div>
        </div>
      </div>

      {/* Tab Bar */}
      <div className="bg-white border-b border-gray-100 sticky top-[52px] z-10">
        <div className="max-w-2xl mx-auto px-4 flex">
          {tabs.map(t => (
            <button key={t.id} onClick={() => setTab(t.id)}
              className={`flex items-center gap-1.5 px-4 py-3 text-xs font-semibold border-b-2 transition-all relative ${
                tab === t.id ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-400 hover:text-gray-600'
              }`}>
              {t.icon}
              <span className="hidden sm:inline">{t.label}</span>
              {t.badge > 0 && (
                <span className="absolute -top-0.5 right-0.5 bg-red-500 text-white text-[9px] font-bold rounded-full h-4 w-4 flex items-center justify-center">{t.badge}</span>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Tab Content */}
      <div className="max-w-2xl mx-auto px-4 py-5 pb-20">
        {tab === 'status' && <StatusTab caseData={caseData} stages={stages} currentStageIndex={currentStageIndex} />}
        {tab === 'messages' && <MessagesTab api={api.current} onExpired={handleExpired} onRefresh={refreshSession} />}
        {tab === 'documents' && <DocumentsTab api={api.current} onExpired={handleExpired} />}
        {tab === 'activity' && <ActivityTab api={api.current} onExpired={handleExpired} />}
      </div>

      {/* Portal branding footer */}
      {portalBranding && (
        <div className="fixed bottom-0 left-0 right-0 bg-white/90 backdrop-blur border-t border-gray-100 py-2 text-center z-10">
          <p className="text-[10px] text-gray-400">{portalBranding}</p>
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════
// STATUS TAB
// ═══════════════════════════════════════════
function StatusTab({ caseData, stages, currentStageIndex }) {
  return (
    <div className="space-y-4">
      {/* Current Status */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5">
        <div className="flex items-center justify-between mb-4">
          <div>
            <p className="text-[10px] text-gray-400 uppercase tracking-wider font-semibold">{caseData?.record_type_name}</p>
            <p className="text-base font-bold text-gray-900 mt-0.5">{caseData?.title}</p>
          </div>
        </div>
        <div className="flex items-center gap-2 mb-5">
          <span className="text-xs font-medium text-gray-500">Current Status:</span>
          <span className="text-sm font-bold px-3 py-1 rounded-full text-white shadow-sm"
            style={{ backgroundColor: caseData?.stage_color || '#6B7280' }}>
            {caseData?.stage_name || 'Unknown'}
          </span>
        </div>

        {/* Progress */}
        {stages.length > 0 && (
          <div className="space-y-0">
            {stages.map((stage, idx) => {
              const isCurrent = idx === currentStageIndex;
              const isCompleted = idx < currentStageIndex;
              const isFuture = idx > currentStageIndex;
              return (
                <div key={stage.id} className="flex items-start gap-3">
                  <div className="flex flex-col items-center">
                    <div className={`h-7 w-7 rounded-full flex items-center justify-center flex-shrink-0 transition-all ${
                      isCompleted ? 'bg-green-500 shadow-sm shadow-green-200' :
                      isCurrent ? 'ring-[3px] ring-blue-100 shadow-sm' : 'bg-gray-200'
                    }`} style={isCurrent ? { backgroundColor: stage.color || '#3B82F6' } : {}}>
                      {isCompleted ? Icons.check : isCurrent ? <div className="h-2.5 w-2.5 rounded-full bg-white" /> : <div className="h-2 w-2 rounded-full bg-gray-400" />}
                    </div>
                    {idx < stages.length - 1 && <div className={`w-0.5 h-8 ${isCompleted ? 'bg-green-300' : 'bg-gray-200'}`} />}
                  </div>
                  <div className={`pt-1 ${isFuture ? 'opacity-35' : ''}`}>
                    <p className={`text-sm font-semibold ${isCurrent ? 'text-gray-900' : isCompleted ? 'text-green-700' : 'text-gray-400'}`}>{stage.name}</p>
                    {isCurrent && <p className="text-xs text-blue-600 font-semibold mt-0.5">← You are here</p>}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════
// MESSAGES TAB
// ═══════════════════════════════════════════
function MessagesTab({ api, onExpired, onRefresh }) {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newMsg, setNewMsg] = useState('');
  const [sending, setSending] = useState(false);
  const bottomRef = useRef(null);
  const inputRef = useRef(null);

  const loadMessages = useCallback(async () => {
    try {
      const data = await api.get('/messages');
      setMessages(data.messages || []);
      onRefresh();
    } catch (err) {
      if (err.expired) onExpired();
    } finally {
      setLoading(false);
    }
  }, [api, onExpired, onRefresh]);

  useEffect(() => { loadMessages(); }, [loadMessages]);
  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);

  // Poll every 10s
  useEffect(() => {
    const interval = setInterval(loadMessages, 10000);
    return () => clearInterval(interval);
  }, [loadMessages]);

  const handleSend = async (e) => {
    e.preventDefault();
    if (!newMsg.trim() || sending) return;
    setSending(true);
    try {
      await api.post('/messages', { body: newMsg.trim() });
      setNewMsg('');
      await loadMessages();
      inputRef.current?.focus();
    } catch (err) {
      if (err.expired) onExpired();
    } finally {
      setSending(false);
    }
  };

  if (loading) return <div className="text-center py-12 text-gray-400 text-sm">Loading messages...</div>;

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden flex flex-col" style={{ height: 'calc(100vh - 200px)', minHeight: '400px' }}>
      {/* Messages List */}
      <div className="flex-1 overflow-y-auto p-4 space-y-3">
        {messages.length === 0 ? (
          <div className="text-center py-12">
            <div className="text-gray-300 mb-2">{Icons.message}</div>
            <p className="text-sm text-gray-400">No messages yet.</p>
            <p className="text-xs text-gray-300 mt-1">Send a message to your team below.</p>
          </div>
        ) : (
          messages.map(msg => {
            const isClient = msg.sender_type === 'client';
            const isSystem = msg.sender_type === 'system';
            if (isSystem) {
              return (
                <div key={msg.id} className="flex justify-center">
                  <div className="bg-gray-50 text-gray-500 text-xs px-3 py-1.5 rounded-full">{msg.body}</div>
                </div>
              );
            }
            return (
              <div key={msg.id} className={`flex ${isClient ? 'justify-end' : 'justify-start'}`}>
                <div className={`max-w-[80%] rounded-2xl px-4 py-2.5 ${
                  isClient ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-900'
                }`}>
                  {!isClient && (
                    <p className="text-[10px] font-semibold text-gray-500 mb-0.5">
                      {msg.sender_first || 'Staff'} {msg.sender_last || ''}
                    </p>
                  )}
                  <p className="text-sm whitespace-pre-wrap">{msg.body}</p>
                  <p className={`text-[10px] mt-1 ${isClient ? 'text-blue-200' : 'text-gray-400'}`}>
                    {timeAgo(msg.created_at)}
                    {msg.channel === 'sms' && ' · via SMS'}
                  </p>
                </div>
              </div>
            );
          })
        )}
        <div ref={bottomRef} />
      </div>

      {/* Input */}
      <form onSubmit={handleSend} className="border-t border-gray-100 p-3 flex gap-2">
        <input ref={inputRef} type="text" value={newMsg} onChange={e => setNewMsg(e.target.value)}
          placeholder="Type a message..." maxLength={5000}
          className="flex-1 px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
        <button type="submit" disabled={!newMsg.trim() || sending}
          className="bg-blue-600 text-white rounded-xl px-4 py-2.5 hover:bg-blue-700 disabled:opacity-40 transition-all">
          {Icons.send}
        </button>
      </form>
    </div>
  );
}

// ═══════════════════════════════════════════
// DOCUMENTS TAB
// ═══════════════════════════════════════════
function DocumentsTab({ api, onExpired }) {
  const [documents, setDocuments] = useState([]);
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadTarget, setUploadTarget] = useState(null); // request_id or null
  const fileInputRef = useRef(null);
  const generalFileRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const [docsData, reqsData] = await Promise.all([
        api.get('/documents'),
        api.get('/document-requests'),
      ]);
      setDocuments(docsData.documents || []);
      setRequests(reqsData.requests || []);
    } catch (err) {
      if (err.expired) onExpired();
    } finally {
      setLoading(false);
    }
  }, [api, onExpired]);

  useEffect(() => { load(); }, [load]);

  const handleDownload = async (docId) => {
    try {
      const data = await api.get('/documents/' + docId + '/download');
      window.open(data.url, '_blank');
    } catch (err) {
      if (err.expired) onExpired();
    }
  };

  const handleUpload = async (file, requestId) => {
    if (!file) return;
    setUploading(true);
    try {
      await api.upload('/documents/upload', file, { request_id: requestId || '' });
      await load();
    } catch (err) {
      if (err.expired) onExpired();
    } finally {
      setUploading(false);
      setUploadTarget(null);
    }
  };

  if (loading) return <div className="text-center py-12 text-gray-400 text-sm">Loading documents...</div>;

  const pendingRequests = requests.filter(r => r.status === 'pending');
  const completedRequests = requests.filter(r => r.status !== 'pending');

  return (
    <div className="space-y-4">
      {/* Pending Requests */}
      {pendingRequests.length > 0 && (
        <div className="bg-amber-50 rounded-2xl border border-amber-200 p-5">
          <h3 className="text-sm font-bold text-amber-800 mb-3">📋 Requested Documents</h3>
          <div className="space-y-3">
            {pendingRequests.map(req => (
              <div key={req.id} className="bg-white rounded-xl p-3 border border-amber-100">
                <p className="text-sm font-semibold text-gray-900">{req.title}</p>
                {req.description && <p className="text-xs text-gray-500 mt-0.5">{req.description}</p>}
                <div className="flex items-center justify-between mt-2">
                  <p className="text-[10px] text-gray-400">Requested {timeAgo(req.created_at)}</p>
                  <label className="cursor-pointer bg-amber-500 hover:bg-amber-600 text-white text-xs font-semibold px-3 py-1.5 rounded-lg transition-colors">
                    Upload
                    <input type="file" className="hidden" onChange={e => handleUpload(e.target.files[0], req.id)} />
                  </label>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Upload General */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5">
        <h3 className="text-sm font-bold text-gray-900 mb-3">Upload a Document</h3>
        <label className={`border-2 border-dashed rounded-xl p-6 text-center cursor-pointer transition-colors block ${
          uploading ? 'border-blue-300 bg-blue-50' : 'border-gray-200 hover:border-blue-300'
        }`}>
          <div className="text-gray-300 mb-2 flex justify-center">{Icons.upload}</div>
          {uploading ? (
            <p className="text-xs text-blue-600 font-semibold">Uploading...</p>
          ) : (
            <>
              <p className="text-xs text-gray-500 font-medium">Tap to select a file</p>
              <p className="text-[10px] text-gray-400 mt-1">PDF, images, Word, Excel — up to 15MB</p>
            </>
          )}
          <input ref={generalFileRef} type="file" className="hidden" disabled={uploading}
            onChange={e => handleUpload(e.target.files[0], null)} />
        </label>
      </div>

      {/* Shared Documents */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5">
        <h3 className="text-sm font-bold text-gray-900 mb-3">Shared Documents</h3>
        {documents.length === 0 ? (
          <p className="text-xs text-gray-400 text-center py-4">No documents shared yet.</p>
        ) : (
          <div className="space-y-2">
            {documents.map(doc => (
              <div key={doc.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors">
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 truncate">{doc.original_name}</p>
                  <p className="text-[10px] text-gray-400">{formatSize(doc.size_bytes)} · {timeAgo(doc.created_at)}</p>
                </div>
                <button onClick={() => handleDownload(doc.id)}
                  className="text-blue-600 hover:text-blue-800 p-2 rounded-lg hover:bg-blue-50 transition-colors ml-2">
                  {Icons.download}
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Completed Requests */}
      {completedRequests.length > 0 && (
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5">
          <h3 className="text-sm font-bold text-gray-900 mb-3">Completed Uploads</h3>
          <div className="space-y-2">
            {completedRequests.map(req => (
              <div key={req.id} className="flex items-center gap-3 p-3 bg-green-50 rounded-xl">
                <svg className="h-4 w-4 text-green-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                <div>
                  <p className="text-sm font-medium text-gray-900">{req.title}</p>
                  <p className="text-[10px] text-gray-400">Uploaded {req.completed_at ? timeAgo(req.completed_at) : ''}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════
// ACTIVITY TAB
// ═══════════════════════════════════════════
function ActivityTab({ api, onExpired }) {
  const [activity, setActivity] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const data = await api.get('/activity');
        setActivity(data.activity || []);
      } catch (err) {
        if (err.expired) onExpired();
      } finally {
        setLoading(false);
      }
    })();
  }, [api, onExpired]);

  if (loading) return <div className="text-center py-12 text-gray-400 text-sm">Loading activity...</div>;

  const actionLabels = {
    stage_changed: { label: 'Status Updated', color: 'bg-blue-500' },
    document_uploaded: { label: 'Document Added', color: 'bg-green-500' },
    portal_document_uploaded: { label: 'You Uploaded a Document', color: 'bg-amber-500' },
    portal_message_received: { label: 'Message Sent', color: 'bg-purple-500' },
    record_created: { label: 'Case Opened', color: 'bg-blue-600' },
  };

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5">
      <h3 className="text-sm font-bold text-gray-900 mb-4">Recent Activity</h3>
      {activity.length === 0 ? (
        <p className="text-xs text-gray-400 text-center py-8">No activity recorded yet.</p>
      ) : (
        <div className="space-y-0">
          {activity.map((item, idx) => {
            const meta = actionLabels[item.action] || { label: item.action, color: 'bg-gray-400' };
            let detail = '';
            try {
              const d = typeof item.details === 'string' ? JSON.parse(item.details) : item.details;
              if (item.action === 'stage_changed') detail = d?.to_stage_name ? 'Moved to ' + d.to_stage_name : '';
              if (item.action === 'document_uploaded' || item.action === 'portal_document_uploaded') detail = d?.filename || '';
              if (item.action === 'portal_message_received') detail = d?.preview || '';
            } catch (e) {}
            return (
              <div key={idx} className="flex items-start gap-3">
                <div className="flex flex-col items-center">
                  <div className={`h-3 w-3 rounded-full ${meta.color} flex-shrink-0 mt-1.5`} />
                  {idx < activity.length - 1 && <div className="w-0.5 h-10 bg-gray-100" />}
                </div>
                <div className="pb-4">
                  <p className="text-sm font-semibold text-gray-900">{meta.label}</p>
                  {detail && <p className="text-xs text-gray-500 mt-0.5">{detail}</p>}
                  <p className="text-[10px] text-gray-400 mt-0.5">{formatDate(item.created_at)}</p>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
